// Team Activity Feed — Data Model & Write Helper
// Story 92: Team Activity Feed
//
// Activity documents live at teams/{teamId}/activity/{activityId}
// Auto-expire after 30 days (expiresAt field).
// All writes are fire-and-forget — never throws to callers.

import { addDoc, collection } from "firebase/firestore";
import { db } from "../config/firebase";

export type TeamActivityType =
  | "step_completed"
  | "step_edited"
  | "ai_analysis_run"
  | "step_assigned"
  | "member_joined"
  | "project_shared";

// Firestore document: teams/{teamId}/activity/{activityId}
export interface TeamActivity {
  id?: string;            // Firestore doc ID — set on read, not stored in doc
  type: TeamActivityType;
  actorUID: string;       // UID of user who triggered this event
  actorEmail: string;     // Email of actor — used for display
  projectID?: string;     // For project-scoped activities (step writes, project shares)
  projectTitle?: string;  // Human-readable project name
  stepNumber?: number;    // For step-related activities
  stepTitle?: string;     // Resolved step title (optional — feed can resolve from stepNumber)
  metadata?: Record<string, unknown>; // Extra context per activity type
  timestamp: string;      // ISO — used for ordering
  expiresAt: string;      // ISO — 30 days from timestamp (for future cleanup)
}

/**
 * Write a team activity record to Firestore.
 * Fire-and-forget — never throws, never blocks the caller.
 */
export async function writeTeamActivity(
  teamId: string,
  activity: Omit<TeamActivity, "id" | "timestamp" | "expiresAt">
): Promise<void> {
  if (!teamId) return;
  try {
    const now = new Date();
    await addDoc(collection(db, "teams", teamId, "activity"), {
      ...activity,
      timestamp: now.toISOString(),
      expiresAt: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    });
  } catch {
    // Activity writes are non-critical — silently ignore failures
  }
}
